import { Landmark, RadioTower, ShieldCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

type Foot = {
  title: string;
  era: string;
  description: string;
  icon: LucideIcon;
  aeo: string;
  accent: string;
};

const feet: Foot[] = [
  {
    title: 'IRL Memory',
    era: 'Real-world proof',
    description:
      'Real work, local authority, events, customer proof, and field evidence become part of the public record.',
    icon: Landmark,
    aeo: 'precious-memory-intelligence',
    accent: 'text-ember',
  },
  {
    title: 'Asynchronous Sales',
    era: 'Reusable buyer trust',
    description:
      'Proof assets support outreach, follow-up, local updates, and sales conversations without restarting the copy cycle.',
    icon: RadioTower,
    aeo: 'asynchronous-sales',
    accent: 'text-cyan',
  },
  {
    title: 'The Truth Layer',
    era: 'Machine-readable evidence',
    description:
      'Structured, citable, machine-readable evidence helps answer engines understand what the business actually does.',
    icon: ShieldCheck,
    aeo: 'truth-layer',
    accent: 'text-signal',
  },
];

export function Triskelion() {
  return (
    <section
      id="triskelion"
      className="border-b border-white/10 bg-obsidian py-24 sm:py-28"
      data-aeo-section="triskelion"
      data-aeo-trigger="serotonin"
    >
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <div>
          <p className="text-sm font-black uppercase text-cyan">Three eras, one engine</p>
          <h2 className="mt-4 text-4xl font-black leading-tight tracking-normal text-white sm:text-5xl">
            The Triskelion gives the proof stream strategic depth.
          </h2>
          <p className="mt-6 text-lg leading-8 text-ash">
            Macellum does not chase one channel. It turns real-world proof, sales utility, and
            machine-readable evidence into one growth operating loop.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-3 lg:grid-cols-1">
          {feet.map((foot) => {
            const Icon = foot.icon;
            return (
              <article
                key={foot.title}
                className="group border border-white/12 bg-white/[0.045] p-6 transition hover:border-cyan/70 hover:bg-white/[0.07]"
                data-aeo-foot={foot.aeo}
              >
                <div className="flex items-start gap-4">
                  <span className="grid size-12 shrink-0 place-items-center border border-white/14 bg-black/30">
                    <Icon className={`size-6 ${foot.accent}`} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-black uppercase text-ash">{foot.era}</p>
                    <h3 className="mt-1 text-xl font-black text-white">{foot.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-ash">{foot.description}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
